// updated homepage with animations, icons, and additional components
import React, { useEffect, useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Container,
  Card,
  Grid,
  CssBaseline,
  Box,
  Divider,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from "@mui/material";
import { Brightness4, Brightness7, ExpandMore, Group, RocketLaunch, WorkspacePremium, TrendingUp, EmojiObjects, SupportAgent } from "@mui/icons-material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { motion } from "framer-motion";

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

export default function Home() {
  const [posts, setPosts] = useState([]);
  const [logos, setLogos] = useState([]);
  const [categories, setCategories] = useState([]);
  const [darkMode, setDarkMode] = useState(true);
  const [loading, setLoading] = useState(true);

  const theme = createTheme({
    palette: {
      mode: darkMode ? "dark" : "light",
      primary: { main: "#FFD700" },
      background: {
        default: darkMode ? "#0f0f0f" : "#f9f9f9",
        paper: darkMode ? "#2a2a2a" : "#ffffff",
      },
      text: {
        primary: darkMode ? "#FFD700" : "#1a1a1a",
        secondary: darkMode ? "#cccccc" : "#4a4a4a",
      },
    },
    typography: {
      fontFamily: '"DM Sans", sans-serif',
    },
  });

  useEffect(() => {
    fetch("https://hireveda.com/ghost/api/content/posts/?key=8a8add75b60634960da9a83a7f")
      .then(res => res.json())
      .then(data => {
        const allPosts = data?.posts || [];
        setPosts(allPosts);

        const clientLogos = allPosts.filter(
          p => Array.isArray(p.tags) && p.tags.some(t => t.name === "Clients")
        );
        setLogos(clientLogos.map(p => p.feature_image).filter(Boolean));

        const catSet = new Set();
        allPosts.forEach(p => (p.tags || []).forEach(t => catSet.add(t.name)));
        setCategories([...catSet]);
      })
      .catch(err => console.error("Ghost fetch error:", err))
      .finally(() => setLoading(false));
  }, []);

  const featureItems = [
    [<RocketLaunch />, "AI-Powered Matching"],
    [<Group />, "Top Talent Network"],
    [<WorkspacePremium />, "Expert-Led Hiring"],
    [<TrendingUp />, "High Conversion Rate"],
    [<EmojiObjects />, "Smart Job Branding"],
    [<SupportAgent />, "Candidate Engagement"]
  ];

  const testimonials = [
    { quote: "HireVeda helped us fill key roles in record time.", name: "Amit, Founder - ScaleTech" },
    { quote: "Fantastic support and quality candidates.", name: "Priya, HR Lead - FinSaaS" },
    { quote: "The best recruitment partner we’ve had.", name: "Rahul, COO - GroWise" }
  ];

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />

      <AppBar position="static" color="transparent" elevation={0}>
        <Toolbar sx={{ justifyContent: "space-between" }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>HireVeda</Typography>
          <div>
            {["Home", "Jobs", "For Recruiters", "Blog", "Contact"].map(label => (
              <Button key={label} color="inherit">{label}</Button>
            ))}
            <IconButton onClick={() => setDarkMode(!darkMode)} color="inherit">
              {darkMode ? <Brightness7 /> : <Brightness4 />}
            </IconButton>
          </div>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 10 }}>
        {/* HERO BANNER */}
        <motion.section variants={fadeIn} initial="hidden" animate="visible">
          <Typography variant="h2" fontWeight={700} textAlign="center" gutterBottom>
            Hire Smarter. Hire Better. <span style={{ color: theme.palette.primary.main }}>HireVeda</span>
          </Typography>
          <Typography variant="h6" textAlign="center" mb={6}>
            A modern hiring platform powered by deep expertise and technology.
          </Typography>
          <Box textAlign="center">
            <Button variant="contained" size="large">Request a Demo</Button>
          </Box>
        </motion.section>

        {/* FEATURES GRID */}
        <motion.section variants={fadeIn} initial="hidden" whileInView="visible" viewport={{ once: true }} mt={10}>
          <Typography variant="h4" fontWeight={600} gutterBottom textAlign="center">
            Why HireVeda?
          </Typography>
          <Grid container spacing={4} mt={1}>
            {featureItems.map(([icon, label], i) => (
              <Grid item xs={6} md={4} key={i}>
                <Card sx={{ p: 3, textAlign: 'center', transition: 'transform 0.3s', '&:hover': { transform: 'scale(1.03)' } }}>
                  <Box mb={2} fontSize={40}>{icon}</Box>
                  <Typography fontWeight={600}>{label}</Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </motion.section>

        {/* TESTIMONIALS CAROUSEL */}
        <motion.section variants={fadeIn} initial="hidden" whileInView="visible" viewport={{ once: true }} mt={12}>
          <Typography variant="h4" fontWeight={600} textAlign="center" gutterBottom>
            What Our Clients Say
          </Typography>
          <Grid container spacing={4} justifyContent="center">
            {testimonials.map((t, i) => (
              <Grid item xs={12} md={4} key={i}>
                <Card sx={{ p: 4 }}>
                  <Typography fontStyle="italic">“{t.quote}”</Typography>
                  <Typography fontWeight={600} mt={2}>{t.name}</Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </motion.section>

        {/* PRICING SECTION */}
        <motion.section variants={fadeIn} initial="hidden" whileInView="visible" viewport={{ once: true }} mt={10}>
          <Typography variant="h4" fontWeight={600} textAlign="center" gutterBottom>
            Transparent Pricing
          </Typography>
          <Grid container spacing={4} mt={1}>
            {[
              ['Starter', 'For early teams', '5 profiles/month', '$499'],
              ['Pro', 'Growing startups', '15 profiles/month', '$1299'],
              ['Enterprise', 'Unlimited hiring', 'Unlimited access', 'Custom']
            ].map(([title, subtitle, feature, price], idx) => (
              <Grid item xs={12} md={4} key={idx}>
                <Card sx={{ p: 4, textAlign: 'center', height: '100%' }}>
                  <Typography variant="h6" fontWeight={700}>{title}</Typography>
                  <Typography variant="body2" color="text.secondary">{subtitle}</Typography>
                  <Typography mt={2} mb={1}>{feature}</Typography>
                  <Typography variant="h4">{price}</Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </motion.section>

        {/* CTA SECTION */}
        <Box textAlign="center" my={10}>
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Ready to Hire Smarter?
          </Typography>
          <Button variant="contained" size="large">Talk to Us</Button>
        </Box>

        {/* FAQ ACCORDION */}
        <motion.section variants={fadeIn} initial="hidden" whileInView="visible" viewport={{ once: true }}>
          <Typography variant="h4" fontWeight={600} textAlign="center" gutterBottom>
            Frequently Asked Questions
          </Typography>
          <Box maxWidth="md" mx="auto">
            {[
              ['What makes HireVeda different?', 'We bring functional expertise + tech to each hiring cycle.'],
              ['Do you charge for shortlisting?', 'No. You only pay when you hire.'],
              ['How fast can I see candidates?', 'Usually within 48 hours of kickoff.']
            ].map(([q, a], i) => (
              <Accordion key={i} defaultExpanded={i === 0}>
                <AccordionSummary expandIcon={<ExpandMore />}><Typography fontWeight={600}>{q}</Typography></AccordionSummary>
                <AccordionDetails><Typography>{a}</Typography></AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </motion.section>
      </Container>
    </ThemeProvider>
  );
}